import { Close } from '@mui/icons-material';
import { Box, Button, Card, CardActions, CardContent, CardHeader, FormControl, Grid, IconButton, InputLabel, MenuItem, Modal, TextField, Typography } from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';
import moment from 'moment';
import { Dispatch, SetStateAction } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useAWSWebsocket } from '../../../../Hooks/useAWSWebsocket';
import { useToast } from '../../../../Hooks/useToast';
import { useUser } from '../../../../Hooks/useUser';
import { clientApi, useGetClientQuery } from '../../../../Redux/api/clientApi';
import { useGetManyJobsQuery, useInviteTalentToJobMutation } from '../../../../Redux/api/jobApi';
import { setJobPostingModalOpen } from '../../../../Redux/slices/JobPostingCreationSlice';
import { useAppDispatch } from '../../../../Redux/store';
import { IMarketplaceJob } from '../../../../Types/Job';
import { KaziButton } from '../../../../Shared Components/Kazi/KaziButton';
import { setShowUnverifiedModal } from '../../../../Redux/slices/GlobalModalSlice';

interface ITalentInviteForm {
    posting: string;
    question1: string;
    question2: string;
    question3: string;
}
interface ITalentInvoiceModalProps {
    open: boolean;
    setOpen: Dispatch<SetStateAction<boolean>>
    clientId: string
}
export const TalentInviteModal = ({open, setOpen, clientId}: ITalentInvoiceModalProps) => {
    /* ******************** Hooks ******************** */  
    const dispatch = useAppDispatch();
    const { sendNotification } = useAWSWebsocket();
    const { isVerified } = useUser();
    const { showToast } = useToast();
    const { locator } = useParams();
    const navigate = useNavigate();
    const {
        control,
        handleSubmit,
        formState: {isDirty, errors}
    } = useForm<ITalentInviteForm>({
        defaultValues: {
            posting: "",
            question1: "",
            question2: "",
            question3: "",
        }
    });
    const {data: clientData, isLoading: clientDataLoading, isFetching: clientDataFetching} = useGetClientQuery(clientId || skipToken); 
    const {data: jobs, isLoading: jobsLoading, isFetching: jobsFetching} = useGetManyJobsQuery(clientData?.posted_jobs || skipToken);
    const [inviteTalent, {data: inviteResponse, isLoading: inviteLoading}] = useInviteTalentToJobMutation();
    /* ******************** Variables ******************** */
    const talentId = locator;
    
    const hasBeenInvitedOrApplied = (job: IMarketplaceJob) => {
        return job.applicants.some( a => a === talentId) || 
        clientData?.talent_invites.some(i => i.job_locator ===  job.locator && i.talent_locator === talentId)
    }
    const applicableJobs: IMarketplaceJob[] = jobs?.filter(j => j.status === 'open' && !hasBeenInvitedOrApplied(j)) ?? []
    /* ******************** Functions ******************** */
    const handleClose = () =>{
        setOpen(false);
    }

    const handlePostJobClicked = () => {
        if (isVerified) {
            dispatch(setJobPostingModalOpen(true));         
            return
        }
        dispatch(setShowUnverifiedModal(true));
    }

    const handleInvite = (data: ITalentInviteForm) => {
        inviteTalent({
            job_locator: data.posting,
            talent_locator: talentId!,
            client_locator: clientId
        })
        .unwrap()
        .then(res =>{
            //TODO: Split out invites from client model
            dispatch(clientApi.util.invalidateTags(['ClientData']));
            showToast({
                text: "Invite sent!"
            })
            handleClose();
        })
        .catch( err => {
            console.log(err)
            showToast({
                text: "Unable to invite talent",
                variant: 'error'
            })
        })
    }
    /* ******************** Effects ******************** */
    /* ******************** JSX ******************** */
    return (
        <Modal
            open={open}
            sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center"
            }}
        >
            <Card
                sx={{
                    width: "600px",
                    borderRadius: "12px",
                    maxHeight: "95%"
                }}
            >
                <CardHeader 
                    sx={{
                        paddingBottom: 1
                    }}                
                    action={
                        <IconButton onClick={handleClose}>
                            <Close color='primary'/>
                        </IconButton>
                    }
                />
                <CardContent
                    sx={{
                        padding: '0 24px'
                    }}
                >
                    {
                        applicableJobs.length === 0?
                        <Grid container display={"flex"} flexDirection={"column"} gap={'20px'}>
                            <Typography variant='lora-title-large' color={'primary'} fontWeight={'bold'}>No jobs to invite this Talent to</Typography> 
                            {clientData?.posted_jobs.length === 0 && <Typography variant='body-medium' color={'primary'} fontWeight={'bold'}>You’ll need to post a job first before you can invite a Talent to apply.</Typography> }
                        </Grid>
                        :
                            <Grid container display={"flex"} flexDirection={"column"} gap={'20px'}>
                            <Typography variant='lora-title-large' color={'primary'} fontWeight={'bold'}>Invite this Talent to apply for a job</Typography> 
                            <Grid item xs display={"flex"} flexDirection={"column"}>
                                    <Typography variant='body-small' color={'black.200'} marginBottom={1}>Job posting</Typography> 
                                    <Controller
                                        name='posting'
                                        control={control}
                                        render={({field}) =>(  
                                            <FormControl>   
                                                <InputLabel>{field.value ?"":"Select job posting"}</InputLabel>                   
                                                <TextField
                                                    {...field}
                                                    select
                                                    // error={hasErrors(field.name, errors)}
                                                    sx={{
                                                        marginBottom: "16px"
                                                    }}
                                                >
                                                    {
                                                        applicableJobs.map((job) => (
                                                            <MenuItem key={job.locator} value={job.locator}>
                                                                {`${job.title} · ${job.type} (Posted ${moment(job.posting_date).format('MMM DD, YYYY')})`}
                                                            </MenuItem>
                                                        ))
                                                    }
                                                </TextField>
                                            </FormControl>
                                        )}
                                    />
                            </Grid>
                            {/* <Typography variant='body-medium'>List any specific questions you want to ask applicants when they apply. You can see their answers when you receive their application.</Typography>
                            <Grid item xs display={"flex"} flexDirection={"column"}>
                                <Typography variant='body-small' color={'black.200'} marginBottom={1}>Question (optional)</Typography> 
                                <Controller
                                    name='question1'
                                    control={control}
                                    render={({field}) =>(
                                        <TextField
                                            placeholder='Add a question for the applicant'
                                            {...field}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs display={"flex"} flexDirection={"column"}>
                                <Typography variant='body-small' color={'black.200'} marginBottom={1}>Question (optional)</Typography> 
                                <Controller
                                    name='question2'
                                    control={control}
                                    render={({field}) =>(
                                        <TextField
                                            placeholder='Add a question for the applicant'
                                            {...field}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs display={"flex"} flexDirection={"column"}>
                                <Typography variant='body-small' color={'black.200'} marginBottom={1}>Question (optional)</Typography>
                                <Controller
                                    name='question3'
                                    control={control}
                                    render={({field}) =>(
                                        <TextField
                                            placeholder='Add a question for the applicant'
                                            {...field}
                                        />
                                    )}
                                />
                            </Grid> */}
                        </Grid>
                    }
                    
                </CardContent>
                <CardActions
                    sx={{
                        padding: '24px'
                    }}
                >
                    <Box flex={1} display={'flex'} flexDirection={'row'} justifyContent={'flex-end'} gap={1}>                            
                        {
                            (clientData?.posted_jobs.length === 0 ) ?
                            <>
                                <Button variant={'outlined'} onClick={handleClose}>Cancel</Button>
                                <Button 
                                    variant={'contained'} 
                                    onClick={handlePostJobClicked}
                                >
                                    Post job
                                </Button>  
                            </>:    
                            <KaziButton 
                                text='Invite'
                                loading={{
                                    isLoading: inviteLoading,
                                    loadingText: 'Inviting talent...',
                                    color: 'secondary'
                                }}
                                variant={'contained'} 
                                disabled={!isDirty || applicableJobs.length === 0} 
                                onClick={handleSubmit(handleInvite)}
                                sx={{
                                    "&.Mui-disabled": {
                                        backgroundColor: 'black.50',
                                        border: 'none',
                                        color: 'primary.main'
                                    }
                                }}
                            />
                        }
                    </Box>
                </CardActions>
            </Card> 
        </Modal>
    )
}